import type { Workflow } from '@servicetim/shared';
import { useCreateWorkflow, useWorkflows } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { FormRow } from '../projects/ui/form-row';

/** Workflows of a project: published version, draft state; create opens the editor. */
export function WorkflowsTable({ projectId }: { projectId: string }) {
  const id = useId();
  const navigate = useNavigate();
  const workflows = useWorkflows(projectId);
  const create = useCreateWorkflow(projectId);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  const open = (workflowId: string) =>
    void navigate({
      to: '/projects/$projectId/workflows/$workflowId',
      params: { projectId, workflowId },
    });

  const submit = async () => {
    if (name.trim() === '') return;
    try {
      const created = await create.mutateAsync({ name: name.trim() });
      toast.success(`Workflow "${created.name}" created with Open and Done`);
      setCreating(false);
      open(created.id);
    } catch {
      // banner
    }
  };

  const columns = useMemo<ColumnDef<Workflow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Workflow',
        cell: ({ row }) => (
          <Link
            to="/projects/$projectId/workflows/$workflowId"
            params={{ projectId, workflowId: row.original.id }}
            className="font-medium text-fg hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'publishedVersion',
        header: 'Published',
        size: 140,
        cell: ({ getValue }) => {
          const v = getValue<number | null>();
          return v === null ? (
            <Badge variant="warning">Never published</Badge>
          ) : (
            <Badge variant="success">v{v}</Badge>
          );
        },
      },
      {
        accessorKey: 'draftDirty',
        header: 'Draft',
        size: 160,
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="info">Unpublished changes</Badge>
          ) : (
            <Badge variant="secondary">In sync</Badge>
          ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        size: 160,
        cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString(),
      },
    ],
    [projectId],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={workflows.data}
        getRowId={(w) => w.id}
        isLoading={workflows.isPending}
        error={workflows.error}
        onRetry={() => void workflows.refetch()}
        onRowClick={(w) => open(w.id)}
        caption="Workflows"
        emptyTitle="No workflows"
        emptyDescription="Issue types need a published workflow; create one to get started."
        toolbar={
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setName('');
                create.reset();
                setCreating(true);
              }}
            >
              <Plus />
              New workflow
            </Button>
          </div>
        }
      />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>New workflow</DialogTitle>
            <DialogDescription>
              Starts as a draft with Open and Done and create / resolve / reopen transitions.
              Publish it before binding an issue type.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {create.error ? <ProblemBanner error={create.error} size="sm" /> : null}
            <FormRow label="Name" htmlFor={`${id}-name`} required>
              <Input
                id={`${id}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                autoFocus
              />
            </FormRow>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || name.trim() === ''}>
                Create and open
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
